import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import debounce from "lodash/debounce"; // Import debounce
import "./Checkout.css";

const BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://127.0.0.1:8000"
    : "https://bonobohope-chazselph.pythonanywhere.com";

const usStates = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const CheckoutStep1 = ({ setCart }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const cart = state?.cart || [];
  const [shippingProcessing, setShippingProcessing] = useState(false);
  const [shippingError, setShippingError] = useState(null);
  const [personalInfo, setPersonalInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [shippingAddress, setShippingAddress] = useState({
    address1: "",
    address2: "",
    city: "",
    region: "Alabama",
    zip: "",
    country: "US",
  });

  useEffect(() => {
    if (!cart || cart.length === 0) {
      navigate("/cart");
    }
  }, [cart, navigate]);

  const handlePersonalInfoChange = (e) => {
    setPersonalInfo({ ...personalInfo, [e.target.name]: e.target.value });
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress({ ...shippingAddress, [name]: value });
  };

  const handleRemoveItem = (indexToRemove) => {
    const updatedCart = cart.filter((_, index) => index !== indexToRemove);
    setCart(updatedCart);
    navigate("/checkout/step1", { state: { cart: updatedCart } });
  };

  const validateFields = () => {
    const { address1, city, region, zip } = shippingAddress;
    const { firstName, lastName, email, phone } = personalInfo;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const zipRegex = /^\d{5}(-\d{4})?$/; // US ZIP: 12345 or 12345-6789
    const phoneRegex = phone ? /^\d{10}$/ : true; // Optional 10-digit phone

    if (!address1 || address1.length < 3)
      return "Address Line 1 must be at least 3 characters.";
    if (!city || city.length < 2) return "City must be at least 2 characters.";
    if (!region) return "State is required.";
    if (!zip || !zipRegex.test(zip))
      return "Invalid ZIP code (e.g., 12345 or 12345-6789).";
    if (!firstName || firstName.length < 2)
      return "First Name must be at least 2 characters.";
    if (!lastName || lastName.length < 2)
      return "Last Name must be at least 2 characters.";
    if (!email || !emailRegex.test(email)) return "Invalid email address.";
    if (phone && !phoneRegex.test(phone))
      return "Phone must be a 10-digit number (if provided).";

    return true;
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const debouncedCheckout = debounce(async (payload) => {
    try {
      const shippingResponse = await fetch(
        `${BASE_URL}/api/shipping-options/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const shippingData = await shippingResponse.json();
      if (!shippingResponse.ok)
        throw new Error("Failed to fetch shipping options.");

      payload.shipping_cost = shippingData.standard.cost;

      const taxResponse = await fetch(`${BASE_URL}/api/calculate-tax/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const taxData = await taxResponse.json();
      if (!taxResponse.ok) throw new Error("Failed to calculate tax.");

      navigate("/checkout/step2", {
        state: {
          cart,
          shippingOptions: shippingData,
          taxCalculation: taxData,
          personalInfo,
          shippingAddress,
        },
      });
    } catch (err) {
      console.error("Checkout error:", err);
      setShippingError(
        "An error occurred while processing your request. Please try again."
      );
    } finally {
      setShippingProcessing(false);
    }
  }, 1000); // 1-second debounce

  const handleContinueCheckout = () => {
    const validationResult = validateFields();
    if (validationResult !== true) {
      setShippingError(validationResult);
      return;
    }
    setShippingProcessing(true);
    setShippingError(null);

    const { zip, address1, city, region } = shippingAddress;
    const { firstName, lastName, email, phone } = personalInfo;
    const payload = {
      zip,
      line_items: cart.map((item) => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
        price: item.price,
      })),
      address_to: {
        first_name: firstName,
        last_name: lastName,
        email,
        phone: phone || "123-456-7890",
        address1,
        address2: shippingAddress.address2 || "",
        city,
        region,
        zip,
        country: shippingAddress.country,
      },
      shipping_cost: 0,
    };

    debouncedCheckout(payload);
  };

  return (
    <div className="checkout-page">
      <div className="checkout-form-container">
        <h2 className="checkout-title">Checkout</h2>
        <form className="checkout-form">
          <div className="form-section">
            <h3 className="section-title">Cart Items</h3>
            {cart.map((item, index) => (
              <div key={index} className="cart-item">
                <img
                  src={item.image}
                  alt={item.title}
                  className="cart-item-image"
                />
                <div className="cart-item-details">
                  <p>{item.title}</p>
                  <p>Quantity: {item.quantity}</p>
                  <p>Price: ${item.price.toFixed(2)}</p>
                </div>
                <button
                  className="remove-button"
                  onClick={() => handleRemoveItem(index)}
                  aria-label={`Remove ${item.title} from cart`}
                >
                  ✕
                </button>
              </div>
            ))}
            <div className="cart-summary">
              <p className="summary-label">Subtotal:</p>
              <p className="summary-value">${calculateSubtotal().toFixed(2)}</p>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Personal Information</h3>
            <div className="input-group">
              <label htmlFor="firstName" className="input-label">
                First Name <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                className="checkout-input"
                value={personalInfo.firstName}
                onChange={handlePersonalInfoChange}
                placeholder="Enter first name"
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="lastName" className="input-label">
                Last Name <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                className="checkout-input"
                value={personalInfo.lastName}
                onChange={handlePersonalInfoChange}
                placeholder="Enter last name"
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="email" className="input-label">
                Email <span className="required-asterisk">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="checkout-input"
                value={personalInfo.email}
                onChange={handlePersonalInfoChange}
                placeholder="Enter email"
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="phone" className="input-label">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className="checkout-input"
                value={personalInfo.phone}
                onChange={handlePersonalInfoChange}
                placeholder="Enter phone number (10 digits)"
              />
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Shipping Address</h3>
            <div className="input-group">
              <label htmlFor="shippingAddress1" className="input-label">
                Address Line 1 <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                id="shippingAddress1"
                name="address1"
                className="checkout-input"
                value={shippingAddress.address1}
                onChange={handleShippingChange}
                placeholder="Enter address"
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="shippingAddress2" className="input-label">
                Address Line 2
              </label>
              <input
                type="text"
                id="shippingAddress2"
                name="address2"
                className="checkout-input"
                value={shippingAddress.address2}
                onChange={handleShippingChange}
                placeholder="Apt, suite, etc. (optional)"
              />
            </div>
            <div className="input-group">
              <label htmlFor="shippingCity" className="input-label">
                City <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                id="shippingCity"
                name="city"
                className="checkout-input"
                value={shippingAddress.city}
                onChange={handleShippingChange}
                placeholder="Enter city"
                required
              />
            </div>
            <div className="input-group state-zip-group">
              <div className="state-zip-field">
                <label htmlFor="shippingRegion" className="input-label">
                  State <span className="required-asterisk">*</span>
                </label>
                <select
                  id="shippingRegion"
                  name="region"
                  className="checkout-input state-zip-input"
                  value={shippingAddress.region}
                  onChange={handleShippingChange}
                  required
                >
                  {usStates.map((stateOption) => (
                    <option key={stateOption} value={stateOption}>
                      {stateOption}
                    </option>
                  ))}
                </select>
              </div>
              <div className="state-zip-field">
                <label htmlFor="shippingZip" className="input-label">
                  ZIP Code <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="shippingZip"
                  name="zip"
                  className="checkout-input zip-input"
                  value={shippingAddress.zip}
                  onChange={handleShippingChange}
                  placeholder="Enter ZIP code (e.g., 12345)"
                  required
                />
              </div>
            </div>
            <div className="input-group">
              <label htmlFor="shippingCountry" className="input-label">
                Country
              </label>
              <input
                type="text"
                id="shippingCountry"
                name="country"
                className="checkout-input"
                value={shippingAddress.country}
                onChange={handleShippingChange}
                disabled
              />
            </div>
            <div className="button-container">
              <button
                type="button"
                className="checkout-button shipping-button"
                onClick={handleContinueCheckout}
                disabled={shippingProcessing}
              >
                {shippingProcessing ? "Processing..." : "Continue Checkout"}
              </button>
            </div>
          </div>
        </form>
        {shippingError && <div className="checkout-error">{shippingError}</div>}
      </div>
      {shippingProcessing && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default CheckoutStep1;
